import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { DatabaseBackup, CloudUpload, RefreshCw, Cloud, CloudOff, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

/** Gestión de respaldos automáticos por empresa + conexión a Google Drive. Solo Master. */
export const BackupManager = () => {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.getBackupStatus(), api.getBackupList()]);
      setStatus(s.data);
      setRuns(l.data || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const d = params.get('drive');
    if (d === 'connected') toast.success('SalesPRO: Google Drive conectado. Los respaldos diarios se subirán automáticamente.');
    else if (d === 'error') toast.error('SalesPRO: No se pudo conectar Google Drive.');
    else if (d === 'notconfigured') toast.error('SalesPRO: Google Drive no está configurado.');
    if (d) window.history.replaceState({}, '', window.location.pathname);
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.runBackupNow();
      toast.success(res.data?.message || 'SalesPRO: Respaldo generado');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al generar respaldo');
    } finally { setRunning(false); }
  };

  const connectDrive = async () => {
    try {
      const res = await api.getDriveAuthUrl();
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Google Drive no está configurado (faltan credenciales)');
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('SalesPRO: ¿Desconectar Google Drive? Los respaldos seguirán guardándose en el servidor.')) return;
    try { await api.disconnectDrive(); toast.success('SalesPRO: Google Drive desconectado'); load(); }
    catch (e) { toast.error('SalesPRO: Error al desconectar'); }
  };

  if (loading || !status) return null;

  const last = status.last_run;

  return (
    <Card className="border border-[#E2E8F0]" data-testid="backup-manager">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <DatabaseBackup size={16} className="text-primary" />
          <h3 className="font-heading font-bold text-[#022C22]">Respaldos automáticos</h3>
        </div>
        <p className="text-xs text-[#64748B] mb-3 flex items-center gap-1">
          <Clock size={12} /> {status.schedule} · un archivo por empresa, guardado en el servidor{status.drive_connected ? ' y en Google Drive' : ''}.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
            <p className="text-[10px] uppercase text-[#94A3B8] mb-0.5">Último respaldo</p>
            <p className="text-sm font-semibold text-[#022C22]" data-testid="backup-last-run">
              {last ? `${new Date(last.ran_at).toLocaleString()} · ${last.count} empresa(s)` : 'Aún no ejecutado'}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
            <p className="text-[10px] uppercase text-[#94A3B8] mb-0.5">Google Drive</p>
            <p className="text-sm font-semibold flex items-center gap-1" data-testid="drive-state">
              {status.drive_connected
                ? <><Cloud size={14} className="text-primary" /> Conectado</>
                : <><CloudOff size={14} className="text-[#94A3B8]" /> {status.drive_configured ? 'No conectado' : 'No configurado'}</>}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
            <p className="text-[10px] uppercase text-[#94A3B8] mb-0.5">Corridas registradas</p>
            <p className="text-sm font-semibold text-[#022C22]">{runs.length}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={runNow} disabled={running} data-testid="run-backup-now">
            <RefreshCw size={14} className={`mr-1.5 ${running ? 'animate-spin' : ''}`} /> Respaldar ahora
          </Button>
          {status.drive_connected ? (
            <Button size="sm" variant="outline" onClick={disconnectDrive} data-testid="disconnect-drive">
              <CloudOff size={14} className="mr-1.5" /> Desconectar Drive
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={connectDrive} disabled={!status.drive_configured} data-testid="connect-drive">
              <CloudUpload size={14} className="mr-1.5" /> Conectar Google Drive
            </Button>
          )}
        </div>
        {!status.drive_configured && (
          <p className="text-[11px] text-[#94A3B8] mt-2">Para subir a Google Drive automáticamente, el administrador debe configurar las credenciales de Google (Client ID/Secret y URI de redirección).</p>
        )}
      </CardContent>
    </Card>
  );
};

export default BackupManager;
